import { useNavigate } from 'react-router-dom';
import type {
  CampaignSummary,
  ExecutionStatusSummary as ExecutionStatusSummaryType,
  MetricDefinition,
} from '@monitoring-panel/types';
import { FIXED_METRICS, OPTIONAL_METRICS } from '@monitoring-panel/types';
import { useState, useRef, useEffect } from 'react';
import { Calendar, User, Users, RefreshCw, ExternalLink, Settings, BarChart3 } from 'lucide-react';
import { StatusSelect } from './StatusSelect';
import { ProgressBar } from './ProgressBar';
import { PacingGauge } from './PacingGauge';
import { MetricsTable } from './MetricsTable';
import { MetricConfigDrawer } from './MetricConfigDrawer';
import { ExecutionStatusSummary } from './ExecutionStatusSummary';
import { ExecutionChecklist, countChecklistItems } from './ExecutionChecklist';
import { CardLinks } from './CardLinks';
import { CardSidePanel } from './CardSidePanel';
import { Modal } from './Modal';
import { useCampaignMetrics, useCampaign, useCampaignSummary, useUpdateExecutionStatus } from '@/hooks/useCampaigns';
import { useExecutionSummary, useChecklist } from '@/hooks/useExecutionNotes';
import {
  useRemoveMetric,
  useMetricConfig,
} from '@/hooks/useMetricConfig';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { ChartWidget } from './dashboard/widgets/ChartWidget';
import { formatDate, formatDecimal, formatNumber, cn } from '@/lib/utils';

/**
 * Map price type to served unit label
 * CPM/vCPM -> Impressions
 * CPC -> Clicks
 * CPV -> Views
 * Default -> Units
 */
function getServedUnitsLabel(priceType?: string): string {
  if (!priceType) {
    return 'Served Units';
  }

  const normalized = priceType.toLowerCase().trim();

  if (normalized.includes('cpm') || normalized.includes('vcpm')) {
    return 'Served Impressions';
  }
  if (normalized.includes('cpc')) {
    return 'Served Clicks';
  }
  if (normalized.includes('cpv')) {
    return 'Served Views';
  }

  return 'Served Units';
}

function ChecklistModal({
  isOpen,
  onClose,
  campaignName,
}: {
  isOpen: boolean;
  onClose: () => void;
  campaignName: string;
}) {
  const { data } = useChecklist(campaignName);
  const counts = data?.checklist?.phases
    ? countChecklistItems(data.checklist.phases)
    : null;
  const title = counts
    ? `Checklist (${counts.done}/${counts.total - counts.na} done)`
    : 'Checklist';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="2xl">
      <ExecutionChecklist campaignName={campaignName} />
    </Modal>
  );
}

/**
 * Hook to track whether an element is visible in the viewport.
 * Once visible, stays true (no unloading on scroll away).
 */
function useIsVisible(rootMargin = '200px') {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return { ref, isVisible };
}

interface CampaignCardProps {
  campaign: CampaignSummary;
  executionSummary?: ExecutionStatusSummaryType;
  isSharedGroup?: boolean;
  viewMode?: 'compact' | 'wide';
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function CampaignCard({
  campaign,
  executionSummary,
  isSharedGroup,
  viewMode = 'compact',
  onRefresh,
  isRefreshing,
}: CampaignCardProps) {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [checklistExpanded, setChecklistExpanded] = useState(false);

  // Lazy loading: fetch GCS-based summary only when card is visible
  const { ref: cardRef, isVisible } = useIsVisible();
  const { data: lazySummaryData, isFetched: summaryFetched } = useCampaignSummary(campaign.executionId, isVisible);

  // Merge lazy-loaded summary into the metadata-only campaign data
  const effectiveCampaign = lazySummaryData?.summary ?? campaign;
  // summaryLoaded = the API call has completed (regardless of whether GCS data exists)
  const summaryLoaded = !isVisible || summaryFetched;

  // Use prop if available, fall back to hook for individual fetching
  const { data: hookSummaryData, isLoading: summaryLoading } = useExecutionSummary(
    executionSummary ? '' : campaign.notesGroupKey
  );
  const effectiveSummary = executionSummary || hookSummaryData?.summary;

  // Fetch metrics and campaign data only when visible
  const { data: metricsData } = useCampaignMetrics(isVisible ? campaign.executionId : '');
  const { data: campaignData } = useCampaign(isVisible ? campaign.executionId : '');
  const { data: configData } = useMetricConfig(isVisible ? campaign.executionId : '');
  const removeMetric = useRemoveMetric(campaign.executionId);
  const updateStatus = useUpdateExecutionStatus(campaign.executionId);

  // Wide view only: fetch the saved dashboard to preview its first chart widgets
  const isWide = viewMode === 'wide';
  const { data: dashboardData } = useDashboardConfig(
    isVisible && isWide ? campaign.executionId : ''
  );

  const isDeal = effectiveCampaign.executionType === 'Deal';

  // For deals: use advertiserBudget/clientSpent metrics; for managed: use units metrics
  const remaining = isDeal
    ? Math.max(0, effectiveCampaign.advertiserBudget - effectiveCampaign.totalClientSpent)
    : Math.max(0, effectiveCampaign.targetUnits - effectiveCampaign.totalUnits);

  // Daily suggested metrics (computed in the API against the last day with
  // data — falls back to remaining when the campaign is on its last day)
  const dailyUnits = isDeal
    ? effectiveCampaign.dailyBudgetSuggested
    : effectiveCampaign.dailyUnitsSuggested;
  const dailyBudget = isDeal
    ? effectiveCampaign.dailyBudgetSuggested
    : effectiveCampaign.todayUnits > 0
      ? Math.max(0, (effectiveCampaign.todayCost / effectiveCampaign.todayUnits) * effectiveCampaign.dailyUnitsSuggested)
      : 0;

  // Calculate budget-based completion for deals
  const budgetCompletionPercentage = effectiveCampaign.advertiserBudget > 0
    ? Math.min(100, (effectiveCampaign.totalClientSpent / effectiveCampaign.advertiserBudget) * 100)
    : 0;

  // Aggregate data for custom metric preview
  const aggregatedData = campaignData?.campaign?.data
    ? aggregateData(campaignData.campaign.data)
    : {};

  const cardAvailableMetrics: MetricDefinition[] = [
    ...FIXED_METRICS,
    ...OPTIONAL_METRICS.filter((m) =>
      configData?.config?.enabledOptionalMetrics?.includes(m.id)
    ),
    ...(configData?.config?.customMetrics || []),
  ];

  // Wide view: first 3 chart widgets from the saved dashboard (those that have data)
  const previewCharts =
    dashboardData?.config?.widgets
      ?.filter((w) => w.type === 'chart')
      .slice(0, 3) ?? [];
  const previewData = campaignData?.campaign?.data ?? [];

  // Get the last day with data
  const lastDayWithData = campaignData?.campaign?.data
    ? getLastDayWithData(campaignData.campaign.data)
    : null;

  return (
    <div
      ref={cardRef}
      data-execution-id={campaign.executionId}
      className={cn(
        'adv-card adv-card--featured',
        isWide && 'lg:flex lg:items-stretch'
      )}
    >
      {/* Main card content (left side in wide mode) */}
      <div className={cn(isWide && 'lg:w-1/2 xl:w-[40%] lg:flex-shrink-0 lg:min-w-0 lg:border-r lg:border-adv-border')}>
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-adv-border">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <button
              onClick={() => navigate(`/campaigns/${campaign.executionId}`)}
              className="text-left group flex items-center gap-2 max-w-full rounded-adv-sm focus-visible:outline-none focus-visible:shadow-adv-focus"
            >
              <h3 className="text-adv-body-m sm:text-adv-body-l font-bold text-adv-primary group-hover:underline truncate">
                {campaign.campaignName}
              </h3>
              <ExternalLink size={16} className="text-adv-tertiary group-hover:text-adv-primary flex-shrink-0 hidden xs:block" />
            </button>
            <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-4 mt-1 text-adv-body-s text-adv-secondary">
              <span className="flex items-center gap-1">
                <Calendar size={14} className="flex-shrink-0" />
                <span className="adv-num truncate">{formatDate(campaign.startDate)} — {formatDate(campaign.endDate)}</span>
              </span>
              <span>
                <span className="adv-num">{effectiveCampaign.daysRemaining}</span> days remaining
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className={cn(
                  'adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0',
                  isRefreshing && 'animate-spin'
                )}
                title="Refresh data"
              >
                <RefreshCw size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <StatusSelect
            status={effectiveCampaign.status}
            onChange={(status) => updateStatus.mutate(status)}
            isPending={updateStatus.isPending}
          />
          <span className="flex items-center gap-1 text-adv-body-s text-adv-secondary truncate">
            <User size={14} className="flex-shrink-0" />
            <span className="truncate">{campaign.specialist}</span>
          </span>
        </div>
      </div>

      {/* Execution Status Summary Badges */}
      <div className="px-3 sm:px-4 py-2 border-b border-adv-border-subtle">
        <div className="flex items-center gap-2">
          {isSharedGroup && (
            <span className="text-adv-tertiary" title="Shared across multiple executions">
              <Users size={14} />
            </span>
          )}
          <ExecutionStatusSummary
            summary={effectiveSummary}
            isLoading={!executionSummary && summaryLoading}
            onChecklistClick={() => setChecklistExpanded(true)}
          />
        </div>
      </div>

      {/* Served Units / Budget Delivered */}
      <div className="p-3 sm:p-4 border-b border-adv-border">
        {!summaryLoaded ? (
          <div className="h-16 flex items-center justify-center">
            <div className="adv-skel h-2 w-full rounded-full" />
          </div>
        ) : (
          <>
            <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1 xs:gap-0 mb-2">
              <span className="adv-kpi__label">
                {isDeal ? 'Budget Delivered' : getServedUnitsLabel(effectiveCampaign.priceType)}
              </span>
              <span className="adv-num text-adv-body-s text-adv-primary">
                {isDeal ? (
                  <>
                    {formatNumber(effectiveCampaign.totalClientSpent, 'euro')} / {formatNumber(effectiveCampaign.advertiserBudget, 'euro')}
                  </>
                ) : (
                  <>
                    {formatNumber(effectiveCampaign.totalUnits, 'number')} / {formatNumber(effectiveCampaign.targetUnits, 'number')}
                  </>
                )}
              </span>
            </div>
            <ProgressBar value={isDeal ? budgetCompletionPercentage : effectiveCampaign.completionPercentage} showLabel={false} />
            <div className="flex justify-between mt-1 text-adv-body-s text-adv-secondary">
              <span>
                <span className="adv-num">
                  {formatDecimal(isDeal ? budgetCompletionPercentage : effectiveCampaign.completionPercentage, 1)}%
                </span>{' '}
                completed
              </span>
              <span className="adv-num">
                {isDeal
                  ? `${formatNumber(remaining, 'euro')} remaining`
                  : `${formatNumber(remaining, 'number')} remaining`}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Campaign Pacing */}
      <div className="p-3 sm:p-4 border-b border-adv-border">
        {!summaryLoaded ? (
          <div className="h-16 flex items-center justify-center">
            <div className="flex gap-4 w-full">
              <div className="adv-skel h-3 flex-1" />
              <div className="adv-skel h-3 flex-1" />
              <div className="adv-skel h-3 flex-1" />
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1 xs:gap-0 mb-3">
              <span className="adv-kpi__label">Campaign Pacing</span>
              <span className="adv-num text-adv-code-xs text-adv-secondary">
                {isDeal ? (
                  <>
                    {formatNumber(effectiveCampaign.todayClientSpent, 'euro')} / {formatNumber(effectiveCampaign.dailyBudgetTarget, 'euro')} | Today
                  </>
                ) : (
                  <>
                    {formatNumber(effectiveCampaign.todayUnits, 'number')} / {formatNumber(effectiveCampaign.dailyTarget, 'number')} | Today
                  </>
                )}
              </span>
            </div>
            <PacingGauge
              accumulated={isDeal ? effectiveCampaign.accumulatedBudgetPacing : effectiveCampaign.accumulatedPacing}
              yesterday={isDeal ? effectiveCampaign.yesterdayBudgetPacing : effectiveCampaign.yesterdayPacing}
              runRate={isDeal ? effectiveCampaign.budgetRunRate : effectiveCampaign.runRate}
            />
          </>
        )}
      </div>

      {/* Key Metrics Table */}
      <div className="p-3 sm:p-4 pb-6">
        <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1 xs:gap-0 mb-3">
          <div className="flex items-center gap-2">
            <h4 className="adv-kpi__label">Key Metrics</h4>
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
              title="Configure metrics & goals"
            >
              <Settings size={14} />
            </button>
          </div>
          {lastDayWithData && (
            <span className="text-adv-body-s text-adv-secondary">
              Last data: <span className="adv-num">{formatDate(lastDayWithData)}</span>
            </span>
          )}
        </div>
        {!summaryLoaded ? (
          <div className="text-adv-body-s text-adv-tertiary">Loading...</div>
        ) : (
          <>
            <div className="flex flex-col xs:flex-row gap-2 xs:gap-4 mb-3 text-adv-body-s">
              <div className="flex items-center gap-1">
                <span className="text-adv-secondary">Daily units:</span>
                <span className="adv-num font-bold text-adv-primary">
                  {isDeal
                    ? formatNumber(dailyUnits, 'euro')
                    : formatNumber(dailyUnits, 'number')}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-adv-secondary">Daily budget:</span>
                <span className="adv-num font-bold text-adv-primary">
                  {formatNumber(dailyBudget, 'euro')}
                </span>
              </div>
            </div>
            {metricsData?.metrics ? (
              <MetricsTable
                metrics={metricsData.metrics}
                onRemoveMetric={(metricId) => removeMetric.mutate(metricId)}
                starredMetrics={configData?.config?.starredMetrics}
                metricGoals={configData?.config?.metricGoals}
              />
            ) : (
              <div className="text-adv-body-s text-adv-tertiary">Loading metrics...</div>
            )}
          </>
        )}
      </div>

      {/* No data warning */}
      {summaryLoaded && !effectiveCampaign.hasData && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
          <div className="bg-adv-gold-crema rounded-adv-md p-2 sm:p-3 text-adv-body-s text-adv-primary">
            No cached data available. Click refresh to load from BigQuery.
          </div>
        </div>
      )}

      {/* Platform Links (always shown so the report "+" is available) */}
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-3 sm:pt-4 border-t border-adv-border mt-auto">
        <CardLinks campaign={campaign} enabled={isVisible} collapsed={isWide} />
      </div>

      </div>
      {/* End main card content */}

      {/* Tabbed side panel: charts, optimizations, metrics per line (wide mode only) */}
      {isWide && (
        <CardSidePanel
          executionId={campaign.executionId}
          enabled={isVisible}
          charts={
            previewCharts.length > 0 && previewData.length > 0 ? (
              <div className="h-full flex flex-col gap-6">
                {previewCharts.map((widget) => (
                  <div key={widget.id} className="flex flex-col flex-1 basis-0 min-h-[140px]">
                    <p className="adv-kpi__label truncate px-1 mb-2">{widget.title}</p>
                    <div className="flex-1 min-h-0">
                      <ChartWidget
                        config={widget}
                        campaignData={previewData}
                        availableMetrics={cardAvailableMetrics}
                        compact
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[120px] text-center text-adv-tertiary">
                <BarChart3 size={24} className="mb-2 opacity-50" />
                <p className="text-adv-body-s">No dashboard charts yet</p>
                <button
                  onClick={() => navigate(`/campaigns/${campaign.executionId}`)}
                  className="adv-link mt-1 text-adv-body-s"
                >
                  Open execution
                </button>
              </div>
            )
          }
        />
      )}

      {/* Checklist Modal */}
      <ChecklistModal
        isOpen={checklistExpanded}
        onClose={() => setChecklistExpanded(false)}
        campaignName={campaign.notesGroupKey}
      />

      {/* Metric config drawer */}
      {campaignData?.campaign?.metadata && (
        <MetricConfigDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          executionId={campaign.executionId}
          metrics={metricsData?.metrics || []}
          availableColumns={campaignData.campaign.availableColumns || []}
          sampleData={aggregatedData}
          metadata={campaignData.campaign.metadata}
          availableMetrics={cardAvailableMetrics}
          campaignData={campaignData.campaign.data || []}
        />
      )}
    </div>
  );
}

function aggregateData(rows: Array<Record<string, unknown>>): Record<string, number> {
  const result: Record<string, number> = {};
  for (const row of rows) {
    for (const [key, value] of Object.entries(row)) {
      if (typeof value === 'number') {
        result[key] = (result[key] || 0) + value;
      }
    }
  }
  return result;
}

function getLastDayWithData(rows: Array<Record<string, unknown>>): string | null {
  if (rows.length === 0) return null;

  const dates = rows
    .map((row) => row.date as string)
    .filter(Boolean)
    .sort();

  return dates.length > 0 ? dates[dates.length - 1] : null;
}
